import React, { useState } from 'react';
import Modal from './Modal';

interface CreatePromptConfirmModalProps {
  isOpen: boolean;
  prompt: string;
  photoCount: number;
  /** Returns to the form with the description focused, so a gap can be filled. */
  onEdit: () => void;
  onConfirm: () => void;
}

type Language = 'en' | 'hi';

interface Copy {
  languageLabel: string;
  title: string;
  intro: string;
  checklist: string[];
  designNote: string;
  promptLabel: string;
  photoNote: (count: number) => string;
  editButton: string;
  confirmButton: string;
}

/**
 * Both languages sit in one object, the way OfferingPage keeps its copy: the
 * English and Hindi wording has to be read side by side whenever this list
 * changes, which a flat key file per language makes harder.
 */
const COPY: Record<Language, Copy> = {
  en: {
    languageLabel: 'English',
    title: 'Before we build your website',
    intro: 'Your website is written from the description below. Please check that it mentions:',
    checklist: [
      'Your business name, exactly as it should appear on the website',
      'What you sell — the main products or services, with prices where you have them',
      'When you are open — days and timings, or whether you work by appointment',
      'Where you are — area and city, and how customers should reach you',
      'What sets you apart — delivery, home service, brands you stock, years in business',
      'Anything specific you want on the website — an offer, a certification, a founder story, customer reviews etc',
      'How it should look and be laid out — the mood you want, colours you have in mind, and which sections matter most',
    ],
    designNote:
      'What you say about the look guides the design we pick for you, chosen from our ready-made themes to match your business.',
    promptLabel: 'Your description',
    photoNote: (count) =>
      `${count} ${count === 1 ? 'photo' : 'photos'} attached — products and gallery images will also come from these.`,
    editButton: 'Edit Prompt',
    confirmButton: 'Create Website',
  },
  hi: {
    languageLabel: 'हिंदी',
    title: 'आपकी वेबसाइट बनाने से पहले',
    intro: 'आपकी वेबसाइट नीचे लिखे विवरण से बनेगी। एक बार देख लें कि इसमें ये बातें लिखी हैं:',
    checklist: [
      'आपके बिज़नेस का नाम, ठीक वैसा जैसा वेबसाइट पर दिखाना है',
      'आप क्या बेचते हैं — मुख्य प्रोडक्ट या सेवाएँ, और जहाँ दाम बताने हों वहाँ दाम भी',
      'आप कब खुले रहते हैं — दिन और समय, या काम अपॉइंटमेंट पर होता है तो वह',
      'आप कहाँ हैं — इलाका और शहर, और ग्राहक आपसे किस तरह संपर्क करें',
      'आपकी खास बात — डिलीवरी, घर पर सेवा, कौन से ब्रांड रखते हैं, कितने साल से काम कर रहे हैं',
      'वेबसाइट पर जो खास चीज़ें दिखानी हैं — कोई ऑफ़र, सर्टिफिकेट, आपकी कहानी, ग्राहकों के रिव्यू आदि',
      'वेबसाइट कैसी दिखे और कैसे सजी हो — कैसा अंदाज़ चाहिए, कौन से रंग पसंद हैं, और कौन से हिस्से सबसे ज़रूरी हैं',
    ],
    designNote:
      'लुक के बारे में आप जो लिखेंगे, उसी के हिसाब से हम आपके बिज़नेस पर जमने वाला डिज़ाइन चुनते हैं — डिज़ाइन हमारी तैयार थीम में से लिया जाता है।',
    promptLabel: 'आपका विवरण',
    photoNote: (count) =>
      `${count} फ़ोटो जोड़ी गई ${count === 1 ? 'है' : 'हैं'} — प्रोडक्ट और गैलरी की तस्वीरें इनसे भी ली जाएँगी।`,
    editButton: 'प्रॉम्प्ट बदलें',
    confirmButton: 'वेबसाइट बनाएँ',
  },
};

const LANGUAGES: Language[] = ['en', 'hi'];

/**
 * Hidden for now, so the dialog shows English only. The Hindi copy and the
 * switcher below are kept ready — set this to true to bring them back.
 */
const LANGUAGE_SWITCHER_ENABLED: boolean = false;

/**
 * Last stop before generation. The AI writes the whole website from the
 * description, and a thin description gives a thin site, so this lists what the
 * business should have covered and shows the text back for one final read.
 */
const CreatePromptConfirmModal: React.FC<CreatePromptConfirmModalProps> = ({
  isOpen,
  prompt,
  photoCount,
  onEdit,
  onConfirm,
}) => {
  const [language, setLanguage] = useState<Language>('en');
  const copy = COPY[language];

  return (
    <Modal isOpen={isOpen} onClose={onEdit} title={copy.title} maxWidthClassName="max-w-xl">
      {/* lang tells the browser to pick a Devanagari face for the Hindi copy,
          since the app's display fonts only cover Latin. */}
      <div lang={language}>
        {LANGUAGE_SWITCHER_ENABLED && (
          <div className="flex justify-end mb-4">
            <div
              className="flex items-center gap-1 p-1 bg-nova-bg3 border border-nova-border rounded-lg"
              role="group"
              aria-label="Language"
            >
              {LANGUAGES.map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => setLanguage(option)}
                  aria-pressed={language === option}
                  lang={option}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    language === option ? 'bg-nova-sky text-nova-sky4' : 'text-nova-text2 hover:text-nova-text'
                  }`}
                >
                  {COPY[option].languageLabel}
                </button>
              ))}
            </div>
          </div>
        )}

        <p className="text-sm text-nova-text2 leading-relaxed">{copy.intro}</p>

        <ul className="mt-4 space-y-2.5">
          {copy.checklist.map((item) => (
            <li key={item} className="flex gap-3 text-sm text-nova-text2">
              <svg
                className="w-4 h-4 mt-1 flex-shrink-0 text-nova-sky"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span className="leading-relaxed">{item}</span>
            </li>
          ))}
        </ul>

        {/* Sets expectations on the last point: the prompt steers which of our
            designs is chosen, and it is not built to an exact specification. */}
        <p className="mt-4 text-xs text-nova-text3 leading-relaxed">{copy.designNote}</p>

        <div className="mt-5">
          <p className="nova-label">{copy.promptLabel}</p>
          <div className="max-h-40 overflow-y-auto rounded-lg border border-nova-border bg-nova-bg2 p-3">
            <p className="text-sm text-nova-text2 whitespace-pre-wrap break-words">{prompt}</p>
          </div>
          {photoCount > 0 && (
            <p className="mt-2 text-xs text-nova-text3">{copy.photoNote(photoCount)}</p>
          )}
        </div>

        <div className="mt-6 flex flex-col-reverse sm:flex-row gap-3">
          <button type="button" onClick={onEdit} className="btn-ghost flex-1 justify-center py-3">
            {copy.editButton}
          </button>
          <button type="button" onClick={onConfirm} className="btn-primary flex-1 justify-center py-3">
            {copy.confirmButton}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default CreatePromptConfirmModal;
